import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { LocationForecastRequest } from 'src/app/core/models/reports/reports.model';
import { IdoReportsService } from 'src/app/core/services/reports/ido/ido-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import {
  CONSTS,
  CSV_FILE_COLUMN_HEADING,
  LocationForecast,
  LocationForecastFormGroup,
} from './location-forecast.constants';

@Component({
  selector: 'app-location-forecast',
  templateUrl: './location-forecast.component.html',
  styleUrls: ['./location-forecast.component.scss'],
  standalone: false,
})
export class LocationForecastComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: LocationForecastFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<LocationForecastComponent> = inject(
    MatDialogRef<LocationForecastComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private idoReportsService: IdoReportsService = inject(IdoReportsService);
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private commonUtils: CommonUtils = inject(CommonUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  locationForecastForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;

  ngOnInit() {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );

    this.locationForecastForm = this.fb.group({
      date: [this.currDate],
    });
  }

  clickExit() {
    this.commonUtils.openExitModal(this.locationForecastForm.dirty);
  }

  /**
   * Used to get location forecast reports
   *
   * @memberof LocationForecastComponent
   */
  getLocationForecastReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const locationCode = user?.location;
          const date = this.locationForecastForm.get('date')?.value;
          const request: LocationForecastRequest = {
            locationCode,
            date: this.commonUtils.getFormattedDate(date),
          };
          return this.idoReportsService.getLocationForecastReport(request).pipe(
            take(1),
            map((data) => {
              const locationForecastvsActual =
                data?.response?.locationForecastvsActual || [];
              if (locationForecastvsActual.length) {
                const customHeader = [
                  [
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    'Location Forecast vs. Actual',
                    '',
                    '',
                    '',
                    '',
                    '',
                  ],
                  [
                    '',
                    'Site Code',
                    `${data?.response.siteCode}`,
                    '',
                    '',
                    'Site Name',
                    `${data?.response.siteName}`,
                    '',
                    '',
                    '',
                    'Date',
                    `${data?.response.date}`,
                  ],
                ];
                const formatedDate = formatDate(
                  new Date(),
                  'MM-dd-yyyy',
                  'en-US',
                );
                const customFooter = [
                  [
                    'TOTAL',
                    '',
                    `${data?.response.totalforecastedDelStops}`,
                    `${data?.response.totalactualDelStops}`,
                    `${data?.response.totaldelStopsFrcstvsStopsDel}`,
                    `${data?.response.totalforecastedDelPkgs}`,
                    `${data?.response.totalactualDelPkgs}`,
                    `${data?.response.totaldelPkgsFrcstvsPkgsActual}`,
                    `${data?.response.totalNumofPkgsDelNotFrcst}`,
                    `${data?.response.totalpercentofFrcstPkgsNotFound}`,
                    `${data?.response.totalPercentofPkgsDelNotFrcst}`,
                    `${data?.response.totalNumofFrcstPkgsNotFound}`,
                  ],
                  ['Printed:', `${formatedDate}`],
                ];
                this.reportUtils.exportCSVReport(
                  locationForecastvsActual,
                  LocationForecast,
                  customHeader,
                  customFooter,
                  CSV_FILE_COLUMN_HEADING,
                );
              } else {
                this.commonUtils.updateBannerType(
                  BANNER_TYPE.NO_DATA_AVAILABLE,
                );
              }
              this.dialogRef.close();
            }),
          );
        }),
      )
      .subscribe();
  }
}
